import moment from 'moment/moment';
import {
  NumberFormatter,
  CurrencyFormatter,
} from '@procore/globalization-toolkit';
import { type ProjectLocation } from '../types/project';

export type BidStatus = 'inProgress' | 'closed' | null;

/** Number of days before due date to show "in progress" status */
const BID_DUE_SOON_THRESHOLD_DAYS = 7;

export interface BidStatusInput {
  bidDueDate?: string | Date;
  open?: boolean;
  acceptPostDueSubmissions?: boolean;
}

export interface BidStatusResult {
  passedBidDueDate: boolean;
  bidDueInDays: number | null;
  status: BidStatus;
  biddingClosed: boolean;
}

const buildCompanySizeText = (business, I18n) => {
  const numberFormatter = new NumberFormatter({
    locale: 'en-US',
  });
  let companySizeText;
  if (!business?.companySize?.start && business?.companySize?.end) {
    companySizeText = I18n.t('components.company_size.low_range_value', {
      end: numberFormatter.formatNumber(business?.companySize?.end + 1),
    });
  } else if (business?.companySize?.start && !business?.companySize?.end) {
    companySizeText = I18n.t('components.company_size.high_range_value', {
      start: numberFormatter.formatNumber(business?.companySize?.start - 1),
    });
  } else {
    companySizeText = I18n.t('components.company_size.medium_range_value', {
      start: numberFormatter.formatNumber(business?.companySize?.start),
      end: numberFormatter.formatNumber(business?.companySize?.end),
    });
  }
  return companySizeText;
};

const buildAvgContractSizeText = (business, I18n) => {
  const currencyFormatter = new CurrencyFormatter({
    locale: 'en-US',
    currencyIsoCode: 'USD',
    minimumFractionDigits: 0,
  });
  let avgContractSizeText;
  if (!business?.avgContractSize?.start && business?.avgContractSize?.end) {
    avgContractSizeText = I18n.t(
      'components.avg_contract_size.low_range_value',
      {
        end: currencyFormatter.formatCurrency(
          business?.avgContractSize?.end + 1
        ),
        currency: 'USD',
      }
    );
  } else if (
    business?.avgContractSize?.start &&
    !business?.avgContractSize?.end
  ) {
    avgContractSizeText = I18n.t(
      'components.avg_contract_size.high_range_value',
      {
        start: currencyFormatter.formatCurrency(
          business?.avgContractSize?.start - 1
        ),
        currency: 'USD',
      }
    );
  } else {
    avgContractSizeText = I18n.t(
      'components.avg_contract_size.medium_range_value',
      {
        start: currencyFormatter.formatCurrency(
          business?.avgContractSize?.start
        ),
        end: currencyFormatter.formatCurrency(business?.avgContractSize?.end),
        currency: 'USD',
      }
    );
  }
  return avgContractSizeText;
};

const buildJoinedProcoreInText = (business, I18n) => {
  if (!business.monolithCreatedAt) {
    return '';
  }
  const momentObject = moment.utc(business.monolithCreatedAt);
  const monthKey = momentObject.format('MMMM').toLowerCase();
  const month = I18n.t(`components.months.${monthKey}`);
  const year = momentObject.format('YYYY');
  return `${I18n.t('components.joined_procore_in')} ${month} ${year}`;
};

const buildActivityDateInText = (business, I18n) => {
  if (!business.monolithCreatedAt) {
    return null;
  }
  const momentObject = moment.utc(business.monolithCreatedAt);
  const monthKey = momentObject.format('MMMM').toLowerCase();
  const month = I18n.t(`components.months.${monthKey}`);
  const year = momentObject.format('YYYY');
  return `${I18n.t('components.since')} ${month} ${year}`;
};

const hasValue = (v: string | null | undefined) =>
  v != null && v !== '' && String(v).trim() !== 'null';

const buildProjectLocationText = (projectLocation: ProjectLocation) => {
  const locationText = [];

  if (hasValue(projectLocation?.address)) {
    locationText.push(projectLocation.address);
  }
  if (hasValue(projectLocation?.city)) {
    locationText.push(projectLocation.city);
  }
  if (hasValue(projectLocation?.stateCode)) {
    if (hasValue(projectLocation?.zip)) {
      locationText.push(`${projectLocation.stateCode} ${projectLocation.zip}`);
    } else {
      locationText.push(projectLocation.stateCode);
    }
  }
  return locationText;
};

const removeSectorUnderscoresAndCapitalize = (sector: string) => {
  return sector
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const PROJECT_SECTOR_TRANSLATION_KEY = 'components.filters.project_sectors';

/**
 * Returns the translated label for a project sector key, or falls back to
 * removeSectorUnderscoresAndCapitalize if no translation exists.
 * @param sectorKey - Raw sector key from API (e.g. "civil_infrastructure", "retail")
 * @param t - i18n translate function (e.g. I18n.t from useI18nContext)
 */
const getProjectSectorLabel = (
  sectorKey: string | null | undefined,
  t: (key: string) => string
): string => {
  if (!sectorKey?.trim()) {
    return '';
  }
  const key = sectorKey.trim().toLowerCase();
  const translationKey = `${PROJECT_SECTOR_TRANSLATION_KEY}.${key}`;
  const translated = t(translationKey);
  if (translated !== translationKey) {
    return translated;
  }
  return removeSectorUnderscoresAndCapitalize(sectorKey);
};

/**
 * Calculates bid due date status for a project
 *
 * Status logic:
 * - 'closed': Project is closed OR (bid date passed AND doesn't accept post-due submissions)
 * - 'inProgress': Bid due date is within next 7 days (not passed) and not closed
 * - null: Bid due date is more than 7 days away and not closed
 *
 * @param project - Object containing bidDueDate, open, and acceptPostDueSubmissions
 * @returns BidStatusResult with passedBidDueDate, bidDueInDays, status, and biddingClosed
 */
const getBidDueStatus = (project: BidStatusInput): BidStatusResult => {
  const passedBidDueDate = project?.bidDueDate
    ? new Date(project.bidDueDate) < new Date()
    : false;

  const bidDueInDays = project?.bidDueDate
    ? moment()
        .startOf('day')
        .diff(moment(project.bidDueDate).startOf('day'), 'days')
    : null;

  const biddingClosed =
    !project.open || (passedBidDueDate && !project.acceptPostDueSubmissions);

  let status: BidStatus = null;
  if (biddingClosed) {
    status = 'closed';
  } else if (
    !passedBidDueDate &&
    bidDueInDays !== null &&
    bidDueInDays >= -BID_DUE_SOON_THRESHOLD_DAYS
  ) {
    status = 'inProgress';
  }

  return {
    passedBidDueDate,
    bidDueInDays,
    status,
    biddingClosed,
  };
};

export {
  buildCompanySizeText,
  buildAvgContractSizeText,
  buildJoinedProcoreInText,
  buildActivityDateInText,
  buildProjectLocationText,
  hasValue,
  removeSectorUnderscoresAndCapitalize,
  getProjectSectorLabel,
  getBidDueStatus,
};
